import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Avatar from "@mui/material/Avatar";
import { Tooltip } from "@mui/material";
import { Client } from "../../../utils/types";

export type ClientDataCardProps = {
  client: Client;
};

export default function ClientDataCard(props: ClientDataCardProps) {
  const { client } = props;

  return (
    <Card
      variant="outlined"
      sx={{
        p: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 48,
        flex: 1,
      }}
    >
      {client && (
        <>
          <Box
            sx={{
              flex: 1,
              minHeight: 125,
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              p: 2,
              overflow: "auto",
              gap: 2,
            }}
          >
            <Avatar
              sx={{
                width: 56,
                height: 56,
                bgcolor: "#1976d2",
                fontSize: 28,
                mr: 2,
              }}
            >
              {(client.clientName ?? "")
                .split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()}
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ lineHeight: 1.1 }}
              >
                {client.clientName}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.2 }}
              >
                {client.clientEmail}
              </Typography>
            </Box>
          </Box>
          <Grid container spacing={3} alignItems="center" sx={{ pl: 7 }}>
            <Grid size={{ xs: 12, sm: 12, md: 12 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ fontSize: 13 }}
              >
                Location
              </Typography>
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{ fontSize: 16 }}
              >
                {client.location}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 2.4 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ fontSize: 13 }}
              >
                Serial Number
              </Typography>
              <Tooltip title={client.sn}>
                <Typography
                  variant="body2"
                  fontWeight={500}
                  sx={{ fontSize: 16, overflow: 'hidden' }}
                >
                  {client.sn}
                </Typography>
              </Tooltip>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 2.4 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ fontSize: 13 }}
              >
                Average Revenue
              </Typography>
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{ fontSize: 16 }}
              >
                $
                {Array.isArray(client.revenue) && client.revenue.length > 0
                  ? (
                      client.revenue.filter((n) => n !== 0).reduce((a, b) => a + b, 0) /
                      (client.revenue.filter((n) => n !== 0).length || 1)
                    ).toLocaleString(undefined, { maximumFractionDigits: 0 })
                  : ""}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 2.4 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ fontSize: 13 }}
              >
                Percentage
              </Typography>
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{ fontSize: 16 }}
              >
                {client.percentage}%
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 1.8 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ fontSize: 13 }}
              >
                Status
              </Typography>
              <Chip
                label={client.status ? "Active" : "Inactive"}
                color={client.status === true ? "success" : "default"}
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: 15,
                  px: 2,
                  py: 1,
                  minWidth: 90,
                  mt: 0.5,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 1.8 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ fontSize: 13 }}
              >
                Created
              </Typography>
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{ fontSize: 16 }}
              >
                {client.startDate
                  ? new Date(client.startDate).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })
                  : ""}
              </Typography>
            </Grid>
          </Grid>
        </>
      )}
    </Card>
  );
}
